import { Field, Form, FormRenderProps } from 'react-final-form'
import React, { useEffect, useState } from 'react'
import moment from 'moment'
import numeral from 'numeral'
import uniqBy from 'lodash/uniqBy'

import { AdminField } from 'components/shared/dropdowns'
import { AutoSaveOnBlur } from 'components/shared/dropdowns/AutoSaveOnFieldBlur'
import {
  Badge,
  CardTableDataCol,
  CardTableRow,
  Chip,
  CommentIcon,
  FormContextProvider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  ValueAutocompleteField,
  makeStyles,
  useCardTable,
  useModal,
  useRouter,
  useSidebar,
  useSnackbar,
  useTheme,
} from '@forgeglobal/design-framework'

import {
  CancelOpportunityMutationOptions,
  InteractionFilter,
  ListingStructureType,
  OpportunityCertainty,
  OpportunityFormInput,
  OpportunityFragment,
  useAccountEntitiesQuery,
  useCreateOrUpdateOpportunityMutation,
  useOpportunityInteractionsQuery,
} from 'graphql/bindings/AdminBindings'

import { DEFAULT_FEE } from 'util/PricingUtils'
import { OpportunityActions } from 'components/opportunities/OpportunityActions'
import { PercentField } from 'components/shared/PercentField'
import { Routes } from 'util/Routes'
import { bidOrAskStructures, opportunityCertainty } from 'util/Enums'
import { formatMoney, formatShares } from 'util/ParsersFormatters'
import { formatPercent, parsePercent } from 'util/NumberUtils'
import {
  getPriceOrValuation,
  parseSizeOrPrice,
  validateFeeField,
  validatePriceOrValuation,
  validateSizeOrSharesField,
} from 'util/FormUtils'
import { opportunityStatusDisplayName } from 'util/OpportunityUtils'
import usePrevious from 'hooks/usePrevious'

import { CancellationModal } from './CancellationModal'
import { CreateDealButton } from './CreateDealButton'
import { DeleteOpportunityModal } from './DeleteOpportunityModal'
import { OpportunityFormCardFooter } from './OpportunityFormCardFooter'
import { OpportunityNotesSidebar } from './OpportunityNotesSidebar'

export interface OpportunityFormCardProps {
  opportunity: OpportunityFragment
  tableVariant: 'account' | 'issuer' | 'dashboard'
  refetchOnCancelMutation?: CancelOpportunityMutationOptions['refetchQueries']
}

type FormShape = {
  id?: string
  structure?: ListingStructureType[]
  owners: { label: string; value: number }[]
  price_or_valuation?: string
  size_or_shares?: string
  entity_id?: number
  certainty: OpportunityCertainty
  latest_activity?: string
  fee?: string
}

const useStyles = makeStyles(() => ({
  directListingChip: {
    width: 35,
  },
}))

export const OpportunityFormCard: React.FC<OpportunityFormCardProps> = ({
  opportunity,
  tableVariant,
  refetchOnCancelMutation,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const [mutate] = useCreateOrUpdateOpportunityMutation()
  const [mutationCount, setMutationCount] = useState(0)

  const { trade_intent } = opportunity

  const {
    fee,
    min_dollar_amount,
    max_dollar_amount,
    min_num_shares,
    max_num_shares,
    valuation,
    share_price,
    size_type,
    pricing_type,
    share_price_recorded_as,
  } = trade_intent

  const createInitialValues = (): FormShape => {
    const getSizeOrShares = () => {
      let result = ''
      const isShares = size_type === 'shares'

      const formattedMinDollarAmount = numeral(min_dollar_amount)
        .format('($0[.]00a)')
        .toUpperCase()
      const formattedMaxDollarAmount = numeral(max_dollar_amount)
        .format('($0[.]00a)')
        .toUpperCase()
      const formattedMinNumShares = numeral(min_num_shares).format('0,0')
      const formattedMaxNumShares = numeral(max_num_shares).format('0,0')

      let min = isShares ? formattedMinNumShares : formattedMinDollarAmount
      let max = isShares ? formattedMaxNumShares : formattedMaxDollarAmount
      const isRange = min !== max

      if (isRange) {
        result += `${min} - ${max}`
      } else {
        result += min
      }

      if (isShares) {
        result += ' shares'
      }

      return result
    }

    return {
      fee: fee !== undefined && fee !== null ? formatPercent(fee) : undefined,
      size_or_shares: getSizeOrShares(),
      price_or_valuation: getPriceOrValuation(pricing_type, share_price, valuation, fee, share_price_recorded_as),
      certainty: opportunity.certainty,
      owners: opportunity?.owners.map(owner => ({ label: owner.name, value: owner.owner_id })),
      structure: trade_intent?.acceptable_structures,
      entity_id: trade_intent?.entity?.id,
    }
  }

  const initialValues = createInitialValues()
  // const [submitValues, setSubmitValues] = useState<FormShape>(initialValues)

  const saveOpportunity = async (values: FormShape) => {
    const parsedFee = values.fee !== undefined ? parsePercent(values.fee) : DEFAULT_FEE
    const sizes = values.size_or_shares ? parseSizeOrPrice(values.size_or_shares, 'size', parsedFee ?? 0) : {}
    const prices = values.price_or_valuation ? parseSizeOrPrice(values.price_or_valuation, 'price', parsedFee ?? 0) : {}

    const validFormShape: OpportunityFormInput = {
      id: opportunity.id,
      account_id: opportunity.account_id,
      company_id: opportunity.company.id,
      type: opportunity.type,
      renew: false, // renew is set to true only when the user explicitly refreshed the opportunity
      fee: parsedFee,
      structure: values.structure,
      status: opportunity.status,
      entity_id: values.entity_id,
      certainty: values.certainty,
      owner_ids: values.owners.filter(owner => !!owner).map(owner => owner!.value),
      ...sizes,
      ...prices,
    }

    return mutate({
      variables: {
        form: validFormShape,
      },
    }).then(() => {
      setMutationCount(mutationCount + 1)
      enqueueSnackbar('Updated opportunity')
    })
  }

  const validate = (values: FormShape) => {
    const { fee, size_or_shares, price_or_valuation } = values
    return {
      fee: validateFeeField(fee),
      size_or_shares: validateSizeOrSharesField(size_or_shares),
      price_or_valuation: validatePriceOrValuation(price_or_valuation, opportunity.type),
    }
  }

  return (
    <Form<FormShape>
      key={mutationCount}
      render={props => (
        <EditOpportunityForm
          opportunity={opportunity}
          tableVariant={tableVariant}
          refetchOnCancelMutation={refetchOnCancelMutation}
          {...props}
        />
      )}
      onSubmit={saveOpportunity}
      validate={validate}
      initialValues={initialValues}
    />
  )
}

const EditOpportunityForm: React.FC<FormRenderProps<FormShape> & {
  opportunity: OpportunityFragment
  tableVariant: 'account' | 'issuer' | 'dashboard'
  refetchOnCancelMutation?: CancelOpportunityMutationOptions['refetchQueries']
}> = ({ handleSubmit, opportunity, tableVariant, refetchOnCancelMutation }) => {
  const theme = useTheme()
  const classes = useStyles()
  const [sidebarExpanded, setSidebarExpanded] = React.useState<boolean>(false)
  const [groupFilter] = useState(InteractionFilter.All)
  const { history } = useRouter()
  const { data: interactionData } = useOpportunityInteractionsQuery({
    variables: { id: opportunity.id, groupFilter },
  })
  const { data: entityData } = useAccountEntitiesQuery({
    variables: {
      id: opportunity.account.id,
    },
  })
  const interactions = interactionData?.opportunity?.interactions ?? []
  const { setRowExpanded, expandedRows } = useCardTable()
  const isCurrentRowExpanded = expandedRows.includes(opportunity.id)
  const previousExpandedRows = usePrevious(expandedRows)
  const isNewlyExpanded = isCurrentRowExpanded && !previousExpandedRows.includes(opportunity.id)

  useEffect(() => {
    // when current row expands
    if (isNewlyExpanded) {
      // open notes panel
      setSidebarExpanded(true)
    }
  }, [isNewlyExpanded])

  const handleOpportunityInteractionButtonClick = () => {
    setRowExpanded(opportunity.id, true)
  }

  const [showDeleteModal, hideDeleteModal] = useModal(() => {
    return (
      <DeleteOpportunityModal
        hideModal={hideDeleteModal}
        opportunityId={opportunity.id}
        pageSource="opportunityDetails"
      />
    )
  }, [opportunity.id])

  const [showCancellationModal, hideCancellationModal] = useModal(
    () => (
      <CancellationModal
        opportunityId={opportunity.id}
        opportunityType={opportunity.type}
        issuerName={opportunity.company.name}
        hideModal={hideCancellationModal}
        refetchQueries={refetchOnCancelMutation}
      />
    ),
    []
  )

  const { company, last_refreshed_at, trade_intent, account, status } = opportunity
  const {
    pricing_type,
    size_type,
    min_dollar_amount,
    max_dollar_amount,
    min_num_shares,
    max_num_shares,
    share_price,
    valuation,
  } = trade_intent
  const { last_round_estimated_number_of_shares } = company
  const refreshedDate = moment(last_refreshed_at).format('MM-DD-YYYY')

  const isRange = min_dollar_amount !== max_dollar_amount || min_num_shares !== max_num_shares
  const impliedPrice = share_price || valuation / last_round_estimated_number_of_shares
  const enoughInformation = !!(last_round_estimated_number_of_shares && (share_price || impliedPrice))
  const shouldDisplayHelperText = tableVariant === 'account' && enoughInformation

  const impliedShares = isRange
    ? `${formatShares(min_dollar_amount / impliedPrice)} - ${formatShares(max_dollar_amount / impliedPrice)} shares`
    : `${formatShares(min_dollar_amount / impliedPrice)} shares`
  const impliedDollars = isRange
    ? `${formatMoney(min_num_shares * impliedPrice)} - ${formatMoney(max_num_shares * impliedPrice)}`
    : `${formatMoney(min_num_shares * impliedPrice)}`

  const sizeHelperText = size_type === 'shares' ? impliedDollars : impliedShares

  const pricingHelperText =
    pricing_type === 'valuation'
      ? `${formatMoney(valuation / last_round_estimated_number_of_shares)} gross pps`
      : `${formatMoney(last_round_estimated_number_of_shares * share_price)} val`

  const isDirectListingTradeIntent =
    opportunity.trade_intent.holding?.cap_table_entry && opportunity.trade_intent.holding?.cap_table_entry.length > 0

  const isLiquidityEventOfferingBid = !!opportunity.liquidity_event

  const entityOptions = uniqBy(entityData?.account?.entities ?? [], 'id').map(entity => ({
    label: `${entity.name} (${entity.type})`,
    value: entity.id,
  }))

  const columns = {
    company_name: (
      <CardTableDataCol name="company_name">
        <Typography variant="subtitle2" onClick={() => history.push(Routes.issuer(company.crm_account.id))}>
          {company.name}
        </Typography>
      </CardTableDataCol>
    ),
    account: (
      <CardTableDataCol name="account">
        <Typography variant="subtitle2" onClick={() => history.push(Routes.account(account.id))}>{`${(
          account.name || 'n/a'
        ).substring(0, 15)}${account.name.length > 15 ? '...' : ''}`}</Typography>
        {isDirectListingTradeIntent && (
          // TODO: Add the ability to differentiate between event types
          <Tooltip placement="top" title="Tender Offer">
            <Chip className={classes.directListingChip} data-cy="liquidity-event-chip" label="TO" />
          </Tooltip>
        )}
        {isLiquidityEventOfferingBid && (
          <Tooltip placement="top" title="Liquidity Event Offering">
            <Chip className={classes.directListingChip} data-cy="liquidity-event-offering-chip" label="LE" />
          </Tooltip>
        )}
      </CardTableDataCol>
    ),
    size_or_shares: (
      <Field
        component={TextField}
        name="size_or_shares"
        label="Size/Shares"
        fullWidth
        helperText={shouldDisplayHelperText && sizeHelperText}
      />
    ),
    price_or_valuation: (
      <Field
        component={TextField}
        name="price_or_valuation"
        label="Price/Valuation"
        helperText={shouldDisplayHelperText && pricingHelperText}
        fullWidth
      />
    ),
    fee: <PercentField name="fee" label="Fee" />,
    last_renewed: (
      <CardTableDataCol name="last_renewed">
        <Typography variant="subtitle2">{refreshedDate}</Typography>
      </CardTableDataCol>
    ),
    createDealButton: <CreateDealButton opportunity={opportunity} />,
    notes: (
      <IconButton aria-label="show-interactions" style={{ marginRight: -12 }}>
        <Badge variant={interactions.length > 0 ? 'dot' : undefined}>
          <CommentIcon onClick={handleOpportunityInteractionButtonClick} />
        </Badge>
      </IconButton>
    ),
    entity: <ValueAutocompleteField name="entity_id" label="Entity" options={entityOptions} />,
    status: (
      <CardTableDataCol name="status">
        <Typography variant="subtitle2">{opportunityStatusDisplayName(status)}</Typography>
      </CardTableDataCol>
    ),
    certainty: <ValueAutocompleteField name="certainty" label="Certainty" options={opportunityCertainty} />,
    owners: <AdminField name="owners" label="Owner(s)" multiple />,
    structure: (
      <ValueAutocompleteField name="structure" options={bidOrAskStructures(opportunity.type)} label="Structure" />
    ),
  }

  return (
    <form onSubmit={handleSubmit}>
      {sidebarExpanded && <OpportunitySidebarEnabler opportunity={opportunity} setExpanded={setSidebarExpanded} />}
      <AutoSaveOnBlur />
      <FormContextProvider value={{ useValidationIcon: false }}>
        <CardTableRow
          id={opportunity.id}
          columns={columns}
          highlightColor={opportunity.source === 'crm_admin' ? theme.palette.primary.main : theme.palette.text.primary}
          actionsMenu={
            <OpportunityActions
              opportunity={opportunity}
              showDeleteOpportunityModal={showDeleteModal}
              showCancellationModal={showCancellationModal}
            />
          }>
          <OpportunityFormCardFooter
            source={opportunity.source}
            createdAt={opportunity.created_at}
            creatorName={`${opportunity.trade_intent.creator.first_name} ${opportunity.trade_intent.creator.last_name}`}
          />
        </CardTableRow>
      </FormContextProvider>
    </form>
  )
}

interface OpportunitySidebarEnablerProps {
  opportunity: OpportunityFragment
  setExpanded: (isExpanded: boolean) => void
}

export const OpportunitySidebarEnabler: React.FC<OpportunitySidebarEnablerProps> = ({ opportunity, setExpanded }) => {
  useSidebar(<OpportunityNotesSidebar opportunity={opportunity} setExpanded={setExpanded} />, [
    opportunity,
    setExpanded,
  ])
  return null
}
